import React, { useState, useEffect } from 'react';
import { createQuestion, getAllQuestions, deleteQuestion, updateQuestion } from '../services/questionsService';
import { useNavigate } from 'react-router-dom';
import UserManagement from '../components/UserManagement';
import CommentManagement from '../components/CommentManagement';
// Import categories data from Categories component
import Categories from '../components/Categories';
import { uploadLogo, getLogoUrl } from '../services/logoService';
import AdminRingManager from '../components/AdminRingManager';
import AdminStoreRingManager from '../components/AdminStoreRingManager';
import AdminRingPackManager from '../components/AdminRingPackManager';
import AdminCollectionManager from '../components/AdminCollectionManager';

function Admin() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [localVotesCleared, setLocalVotesCleared] = useState(false);

  // Active tab state
  const [activeTab, setActiveTab] = useState('questions'); // 'questions', 'comments', 'users', 'settings', 'rings'
  
  // Ring management modals
  const [showAdminRingManager, setShowAdminRingManager] = useState(false);
  const [showStoreRingManager, setShowStoreRingManager] = useState(false);
  const [showRingPackManager, setShowRingPackManager] = useState(false);
  const [showCollectionManager, setShowCollectionManager] = useState(false);
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState('');
  const [logoSize, setLogoSize] = useState(100); // Default to 100%
  const [logoSizeSaved, setLogoSizeSaved] = useState(false);

  // Define available categories for question assignment
  // These match the categories in the Categories component
  const availableCategories = [
    { id: 1, name: 'Life', color: '#FF6B00', emoji: '⚛️' },
    { id: 2, name: 'Food', color: '#E67E22', emoji: '🍔' },
    { id: 3, name: 'Dating', color: '#FFC107', emoji: '❤️' },
    { id: 4, name: 'Fashion', color: '#E91E63', emoji: '👗' },
    { id: 5, name: 'Politics', color: '#2196F3', emoji: '🗳️' },
    { id: 6, name: 'Sports', color: '#4CAF50', emoji: '⚽' },
    { id: 7, name: 'Technology', color: '#9C27B0', emoji: '📱' },
    { id: 8, name: 'Movies', color: '#FF5722', emoji: '🎬' },
    { id: 9, name: 'Music', color: '#607D8B', emoji: '🎵' },
    { id: 10, name: 'Out of the Box', color: '#3F51B5', emoji: '💭' }
  ];

  // Global settings state
  const [globalSettings, setGlobalSettings] = useState({
    bannedWords: '',
    isLoading: false,
    saveSuccess: false,
    saveError: ''
  });

  // Initial form data with leaderboard options
  const [formData, setFormData] = useState({
    questionText: '',
    optionA: '',
    optionB: '',
    questionType: 'standard', // Default to standard poll
    category: '', // Category assignment for the question
    styles: {
      optionABackgroundColor: 'white',
      optionATextColor: 'black',
      optionBBackgroundColor: 'black',
      optionBTextColor: 'white'
    },
    // Leaderboard specific options
    leaderboardOptions: {
      showTomatoCount: true,
      showClapCount: false,
      limit: 3,
      sortBy: 'tomatoes',
      whitelist: '',
      bannedWords: ''
    },
    // Leaderboard styling
    leaderboardStyles: {
      backgroundColor: 'white',
      textColor: 'black',
      borderColor: '#e0e0e0',
      borderRadius: '50px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
      padding: '12px 24px',
      fontSize: '16px',
      fontWeight: '500',
      hoverBackgroundColor: '#f5f5f5',
      activeBackgroundColor: '#e0e0e0'
    }
  });

  // State for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  // Check for admin access in localStorage
  useEffect(() => {
    // Always force login by starting with isAdmin set to false
    setIsAdmin(false);

    // Clear any stored credentials
    localStorage.removeItem('adminAccess');
    sessionStorage.removeItem('adminAccess');
  }, []);

  // Load global settings when the component mounts and user is admin
  useEffect(() => {
    if (!isAdmin) return;

    try {
      // Load banned words from localStorage
      const storedBannedWords = localStorage.getItem('globalBannedWords') || '';

      setGlobalSettings(prev => ({
        ...prev,
        bannedWords: storedBannedWords
      }));

      console.log('Loaded global banned words from localStorage');

      // In a real implementation, you would load from Firestore here
      // const settingsDoc = await getDoc(doc(db, 'settings', 'global'));
      // if (settingsDoc.exists()) {
      //   const data = settingsDoc.data();
      //   setGlobalSettings(prev => ({
      //     ...prev,
      //     bannedWords: data.bannedWords || ''
      //   }));
      // }
    } catch (error) {
      console.error('Error loading global settings:', error);
    }
  }, [isAdmin]);
  const [imageFile, setImageFile] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'image' or 'video'
  const [thumbnailFile, setThumbnailFile] = useState(null); // Thumbnail for videos
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [allQuestions, setAllQuestions] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hasMoreQuestions, setHasMoreQuestions] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  
  // Handle admin password verification
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    // Simple fixed password (you can change this to any password you prefer)
    console.log("Checking password:", adminPassword);
    if (adminPassword === 'admin123') {
      console.log("Password correct - granting access");
      setIsAdmin(true);
    } else {
      console.log("Password incorrect");
      alert('Incorrect password! Please try again.');
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    console.log("Logging out");
    setIsAdmin(false);
    // No need to remove from storage since we're not storing it now
  };

  // Handle global settings changes
  const handleGlobalSettingsChange = (e) => {
    const { name, value } = e.target;
    setGlobalSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle saving global settings
  const handleSaveGlobalSettings = async (e) => {
    e.preventDefault();

    setGlobalSettings(prev => ({
      ...prev,
      isLoading: true,
      saveSuccess: false,
      saveError: ''
    }));

    try {
      // Store banned words in localStorage for now (will be moved to Firestore in a real implementation)
      localStorage.setItem('globalBannedWords', globalSettings.bannedWords);

      // In a real implementation, you would save to Firestore here
      // await setDoc(doc(db, 'settings', 'global'), {
      //   bannedWords: globalSettings.bannedWords,
      //   updatedAt: serverTimestamp()
      // });

      console.log('Global settings saved:', globalSettings);

      // Show success message
      setGlobalSettings(prev => ({
        ...prev,
        isLoading: false,
        saveSuccess: true
      }));

      // Hide success message after 3 seconds
      setTimeout(() => {
        setGlobalSettings(prev => ({
          ...prev,
          saveSuccess: false
        }));
      }, 3000);
    } catch (error) {
      console.error('Error saving global settings:', error);

      setGlobalSettings(prev => ({
        ...prev,
        isLoading: false,
        saveError: error.message || 'Failed to save settings'
      }));
    }
  };

  // Load all questions when the component mounts
  useEffect(() => {
    // Only fetch questions if user is admin
    if (!isAdmin) {
      return;
    }
    
    const fetchAllQuestions = async () => {
      try {
        setLoadingQuestions(true);
        console.log('Admin component: Fetching all questions...');
        
        const questionsData = await getAllQuestions(1, 20);
        console.log('Admin component: Questions data received:', questionsData);
        
        if (questionsData.questions && questionsData.questions.length > 0) {
          console.log(`Admin component: Found ${questionsData.questions.length} questions`);
          setAllQuestions(questionsData.questions);
          setHasMoreQuestions(questionsData.hasMore);
        } else {
          console.log('Admin component: No questions found in data');
          setAllQuestions([]);
          setHasMoreQuestions(false);
        }
        
        setPage(1);
      } catch (error) {
        console.error('Error fetching questions:', error);
      } finally {
        setLoadingQuestions(false);
      }
    };
    
    fetchAllQuestions();
  }, [isAdmin]);
  
  // Load more questions
  const loadMoreQuestions = async () => {
    if (!hasMoreQuestions) return;
    
    try {
      setLoadingQuestions(true);
      const nextPage = page + 1;
      const moreQuestionsData = await getAllQuestions(nextPage, 20);
      
      setAllQuestions(prevQuestions => [...prevQuestions, ...moreQuestionsData.questions]);
      setHasMoreQuestions(moreQuestionsData.hasMore);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more questions:', error);
    } finally {
      setLoadingQuestions(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Handling form change for field: ${name} with value: ${value}`);

    // Handle nested style properties
    if (name.startsWith('style_')) {
      const styleProperty = name.replace('style_', '');
      setFormData(prev => ({
        ...prev,
        styles: {
          ...prev.styles,
          [styleProperty]: value
        }
      }));
    }
    // Handle leaderboard options properties
    else if (name.startsWith('leaderboardOptions_')) {
      const optionProperty = name.replace('leaderboardOptions_', '');
      console.log(`Updating leaderboardOptions.${optionProperty} to ${value}`);

      // Convert string values to appropriate types
      let processedValue = value;
      if (value === 'true') processedValue = true;
      if (value === 'false') processedValue = false;
      if (['limit'].includes(optionProperty)) processedValue = parseInt(value, 10);

      setFormData(prev => ({
        ...prev,
        leaderboardOptions: {
          ...prev.leaderboardOptions || {},
          [optionProperty]: processedValue
        }
      }));
    }
    // Handle leaderboard styles properties
    else if (name.startsWith('leaderboardStyles_')) {
      const styleProperty = name.replace('leaderboardStyles_', '');
      console.log(`Updating leaderboardStyles.${styleProperty} to ${value}`);

      setFormData(prev => ({
        ...prev,
        leaderboardStyles: {
          ...prev.leaderboardStyles || {},
          [styleProperty]: value
        }
      }));
    }
    else {
      // Handle regular properties
      console.log(`Updating regular property ${name} to ${value}`);

      // Special case for questionType to ensure leaderboard options and styles exist
      if (name === 'questionType' && value === 'leaderboard') {
        console.log('Initializing leaderboard options and styles');
        setFormData(prev => ({
          ...prev,
          [name]: value,
          leaderboardOptions: prev.leaderboardOptions || {
            showTomatoCount: true,
            showClapCount: false,
            limit: 3,
            sortBy: 'tomatoes',
            whitelist: '',
            bannedWords: ''
          },
          leaderboardStyles: prev.leaderboardStyles || {
            backgroundColor: 'white',
            textColor: 'black',
            borderColor: '#e0e0e0',
            borderRadius: '50px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            hoverBackgroundColor: '#f5f5f5',
            activeBackgroundColor: '#e0e0e0'
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };
  
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      console.log('File selected:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      // Validate file size
      const maxImageSize = 5 * 1024 * 1024; // 5MB
      const maxVideoSize = 50 * 1024 * 1024; // 50MB

      // Determine if it's an image or video
      if (file.type.startsWith('image/')) {
        if (file.size > maxImageSize) {
          alert(`Image file is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 5MB.`);
          e.target.value = ''; // Reset input
          return;
        }
        setMediaType('image');
        setImageFile(file);
        console.log('Image file accepted');
      } else if (file.type.startsWith('video/')) {
        if (file.size > maxVideoSize) {
          alert(`Video file is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 50MB.`);
          e.target.value = ''; // Reset input
          return;
        }
        setMediaType('video');
        setImageFile(file);
        console.log('Video file accepted');
      } else {
        alert(`Unsupported file type: ${file.type}. Please upload an image or video file.`);
        e.target.value = ''; // Reset input
        return;
      }
    }
  };

  const handleThumbnailChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      console.log('Thumbnail file selected:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      // Validate it's an image
      if (!file.type.startsWith('image/')) {
        alert('Thumbnail must be an image file');
        e.target.value = '';
        return;
      }

      // Validate size (2MB max for thumbnails)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`Thumbnail is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 2MB.`);
        e.target.value = '';
        return;
      }

      setThumbnailFile(file);
      console.log('Thumbnail accepted');
    }
  };

  // Function to handle editing a question
  const handleEditQuestion = (question) => {
    console.log('Editing question:', question);

    // Set edit mode
    setIsEditMode(true);
    setEditingQuestionId(question.id);

    // Populate the form with the question data
    setFormData({
      questionText: question.questionText || '',
      optionA: question.optionA || '',
      optionB: question.optionB || '',
      questionType: question.questionType || 'standard',
      category: question.category || '',
      styles: {
        optionABackgroundColor: question.styles?.optionABackgroundColor || 'white',
        optionATextColor: question.styles?.optionATextColor || 'black',
        optionBBackgroundColor: question.styles?.optionBBackgroundColor || 'black',
        optionBTextColor: question.styles?.optionBTextColor || 'white'
      },
      leaderboardOptions: {
        showTomatoCount: question.leaderboardOptions?.showTomatoCount !== false,
        showClapCount: question.leaderboardOptions?.showClapCount === true,
        limit: question.leaderboardOptions?.limit || 3,
        sortBy: question.leaderboardOptions?.sortBy || 'tomatoes',
        whitelist: question.leaderboardOptions?.whitelist || '',
        bannedWords: question.leaderboardOptions?.bannedWords || ''
      },
      leaderboardStyles: {
        backgroundColor: question.leaderboardStyles?.backgroundColor || 'white',
        textColor: question.leaderboardStyles?.textColor || 'black',
        borderColor: question.leaderboardStyles?.borderColor || '#e0e0e0',
        borderRadius: question.leaderboardStyles?.borderRadius || '50px',
        boxShadow: question.leaderboardStyles?.boxShadow || '0 4px 15px rgba(0,0,0,0.1)',
        padding: question.leaderboardStyles?.padding || '12px 24px',
        fontSize: question.leaderboardStyles?.fontSize || '16px',
        fontWeight: question.leaderboardStyles?.fontWeight || '500',
        hoverBackgroundColor: question.leaderboardStyles?.hoverBackgroundColor || '#f5f5f5',
        activeBackgroundColor: question.leaderboardStyles?.activeBackgroundColor || '#e0e0e0'
      }
    });

    // Scroll to the form
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingQuestionId(null);

    // Reset form
    setFormData({
      questionText: '',
      optionA: '',
      optionB: '',
      questionType: 'standard',
      category: '',
      styles: {
        optionABackgroundColor: 'white',
        optionATextColor: 'black',
        optionBBackgroundColor: 'black',
        optionBTextColor: 'white'
      },
      leaderboardOptions: {
        showTomatoCount: true,
        showClapCount: false,
        limit: 3,
        sortBy: 'tomatoes',
        whitelist: '',
        bannedWords: ''
      },
      leaderboardStyles: {
        backgroundColor: 'white',
        textColor: 'black',
        borderColor: '#e0e0e0',
        borderRadius: '50px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        padding: '12px 24px',
        fontSize: '16px',
        fontWeight: '500',
        hoverBackgroundColor: '#f5f5f5',
        activeBackgroundColor: '#e0e0e0'
      }
    });
    setImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form based on question type
    if (!formData.questionText) {
      setError('Please fill in the question text');
      return;
    }

    if (!isEditMode && !imageFile) {
      setError('Please upload an image');
      return;
    }

    // Only validate optionA and optionB for standard questions
    if (formData.questionType === 'standard' && (!formData.optionA || !formData.optionB)) {
      setError('Please fill in both option A and option B for standard polls');
      return;
    }

    // For leaderboard questions, optionA and optionB aren't required
    // But let's set them to default values for backward compatibility
    let submissionData = {...formData};
    if (formData.questionType === 'leaderboard') {
      // Use default placeholder values for options since they won't be displayed
      if (!submissionData.optionA) submissionData.optionA = 'Auto-populated';
      if (!submissionData.optionB) submissionData.optionB = 'Auto-populated';

      console.log('Preparing leaderboard question with auto-populated options');
    }

    setLoading(true);
    setError('');

    try {
      if (isEditMode) {
        // Update existing question
        console.log('Updating question with data:', submissionData);
        console.log('New image file:', imageFile);

        const updatedQuestion = await updateQuestion(editingQuestionId, submissionData, imageFile);
        console.log('Question updated successfully:', updatedQuestion);

        // Update the question in our state
        setAllQuestions(prevQuestions =>
          prevQuestions.map(q =>
            q.id === editingQuestionId ? updatedQuestion : q
          )
        );

        alert('Question updated successfully!');

        // Exit edit mode
        setIsEditMode(false);
        setEditingQuestionId(null);
      } else {
        // Create new question
        console.log('Creating question with data:', submissionData);
        console.log('Image file:', imageFile);

        const newQuestion = await createQuestion(submissionData, imageFile, thumbnailFile);
        console.log('Question created successfully:', newQuestion);

        // Add the new question to our state
        setAllQuestions(prevQuestions => [newQuestion, ...prevQuestions]);

        alert('Question created successfully! Go to the home page to see it in the question carousel.');
      }

      // Reset form after successful submission
      setFormData({
        questionText: '',
        optionA: '',
        optionB: '',
        questionType: 'standard',
        category: '', // Reset category
        styles: {
          optionABackgroundColor: 'white',
          optionATextColor: 'black',
          optionBBackgroundColor: 'black',
          optionBTextColor: 'white'
        },
        // Reset leaderboard options
        leaderboardOptions: {
          showTomatoCount: true,
          showClapCount: false,
          limit: 3,
          sortBy: 'tomatoes',
          whitelist: '',
          bannedWords: ''
        },
        // Reset leaderboard styling
        leaderboardStyles: {
          backgroundColor: 'white',
          textColor: 'black',
          borderColor: '#e0e0e0',
          borderRadius: '50px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          padding: '12px 24px',
          fontSize: '16px',
          fontWeight: '500',
          hoverBackgroundColor: '#f5f5f5',
          activeBackgroundColor: '#e0e0e0'
        }
      });
      setImageFile(null);

    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} question:`, error);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} question: ${error.message || 'Unknown error'}`);

      // Additional debug info
      if (error.code) {
        console.error('Error code:', error.code);
      }

      alert(`Error: ${error.message || 'Unknown error'}. Check browser console for details.`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteQuestion = async (questionId, questionText) => {
    if (!questionId) return;
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the question: "${questionText}"?`
    );
    
    if (!confirmDelete) return;
    
    setDeleteLoading(true);
    
    try {
      await deleteQuestion(questionId);
      
      // Remove the deleted question from the state
      setAllQuestions(prevQuestions => 
        prevQuestions.filter(question => question.id !== questionId)
      );
      
      alert('Question deleted successfully!');
    } catch (error) {
      console.error('Error deleting question:', error);
      alert(`Failed to delete question: ${error.message || 'Unknown error'}`);
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // Function to clear local voting record
  const handleClearLocalVotes = () => {
    localStorage.removeItem('votedQuestions');
    setLocalVotesCleared(true);
    
    // Reset the notification after 3 seconds
    setTimeout(() => {
      setLocalVotesCleared(false);
    }, 3000);
  };
  
  // Load current logo URL and size
  useEffect(() => {
    if (isAdmin && activeTab === 'settings') {
      const fetchLogoUrl = async () => {
        try {
          const url = await getLogoUrl();
          setCurrentLogoUrl(url);
          
          // Load saved logo size from localStorage
          const savedLogoSize = localStorage.getItem('logoSize');
          if (savedLogoSize) {
            setLogoSize(parseInt(savedLogoSize));
          }
        } catch (error) {
          console.error('Error fetching logo URL:', error);
        }
      };
      
      fetchLogoUrl();
    }
  }, [isAdmin, activeTab]);
  
  // Handle logo file selection
  const handleLogoFileChange = (e) => {
    if (e.target.files[0]) {
      setLogoFile(e.target.files[0]);
    }
  };
  
  // Handle logo upload
  const handleLogoUpload = async (e) => {
    e.preventDefault();
    
    if (!logoFile) {
      setLogoUploadError('Please select a logo file to upload');
      return;
    }
    
    setLogoUploading(true);
    setLogoUploadSuccess(false);
    setLogoUploadError('');
    
    try {
      // Upload with max size of 150px
      const logoUrl = await uploadLogo(logoFile, 150);
      setCurrentLogoUrl(logoUrl);
      setLogoUploadSuccess(true);
      setLogoFile(null);
      
      // Dispatch event to update logo in header
      window.dispatchEvent(new Event('logoUpdated'));
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setLogoUploadSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error uploading logo:', error);
      setLogoUploadError('Failed to upload logo: ' + (error.message || 'Unknown error'));
    } finally {
      setLogoUploading(false);
    }
  };
  
  // Handle logo size save
  const handleSaveLogoSize = async () => {
    try {
      // Save logo size to localStorage
      localStorage.setItem('logoSize', logoSize.toString());
      
      // Show success message
      setLogoSizeSaved(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setLogoSizeSaved(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving logo size:', error);
    }
  };
  
  // Show login form if not admin
  if (!isAdmin) {
    return (
      <div className="admin-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="admin-card" style={{ textAlign: 'center', maxWidth: '400px', padding: '30px' }}>
          <h2 className="admin-card-title">Admin Login</h2>
          <p style={{ marginBottom: '20px' }}>Enter admin password to continue</p>
          
          <form onSubmit={handlePasswordSubmit} style={{ marginBottom: '20px' }}>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Enter password"
              style={{ 
                padding: '12px',
                width: '100%',
                borderRadius: '4px',
                border: '1px solid #ddd',
                marginBottom: '15px',
                fontSize: '16px'
              }}
            />
            <button 
              type="submit" 
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#FF6B00',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Login as Admin
            </button>
          </form>
          
          <div style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <a href="/" style={{ 
              display: 'inline-block', 
              color: '#666',
              textDecoration: 'none'
            }}>
              Return to Home Page
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h1 style={{ margin: 0 }}>Admin Panel</h1>
          <div>
            <button 
              onClick={handleClearLocalVotes}
              style={{ marginRight: '10px', padding: '5px 10px' }}
            >
              {localVotesCleared ? "✓ Votes Reset" : "Reset Votes"}
            </button>
            <a href="/" style={{ marginRight: '10px', padding: '5px 10px', display: 'inline-block' }}>
              View Site
            </a>
            <button 
              onClick={handleLogout}
              style={{ padding: '5px 10px' }}
            >
              Logout
            </button>
          </div>
        </div>
        
        <div style={{ borderBottom: '1px solid #ddd', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => setActiveTab('questions')}
              style={{ 
                padding: '8px 15px', 
                border: 'none',
                backgroundColor: activeTab === 'questions' ? '#f0f0f0' : 'transparent',
                borderBottom: activeTab === 'questions' ? '2px solid #FF6B00' : 'none',
                cursor: 'pointer',
                fontWeight: activeTab === 'questions' ? 'bold' : 'normal'
              }}
            >
              Questions
            </button>
            <button
              onClick={() => setActiveTab('comments')}
              style={{
                padding: '8px 15px',
                border: 'none',
                backgroundColor: activeTab === 'comments' ? '#f0f0f0' : 'transparent',
                borderBottom: activeTab === 'comments' ? '2px solid #FF6B00' : 'none',
                cursor: 'pointer',
                fontWeight: activeTab === 'comments' ? 'bold' : 'normal'
              }}
            >
              Takes
            </button>
            <button
              onClick={() => setActiveTab('users')}
              style={{
                padding: '8px 15px',
                border: 'none',
                backgroundColor: activeTab === 'users' ? '#f0f0f0' : 'transparent',
                borderBottom: activeTab === 'users' ? '2px solid #FF6B00' : 'none',
                cursor: 'pointer',
                fontWeight: activeTab === 'users' ? 'bold' : 'normal'
              }}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              style={{
                padding: '8px 15px',
                border: 'none',
                backgroundColor: activeTab === 'settings' ? '#f0f0f0' : 'transparent',
                borderBottom: activeTab === 'settings' ? '2px solid #FF6B00' : 'none',
                cursor: 'pointer',
                fontWeight: activeTab === 'settings' ? 'bold' : 'normal'
              }}
            >
              Settings
            </button>
            <button
              onClick={() => setActiveTab('rings')}
              style={{
                padding: '8px 15px',
                border: 'none',
                backgroundColor: activeTab === 'rings' ? '#f0f0f0' : 'transparent',
                borderBottom: activeTab === 'rings' ? '2px solid #FF6B00' : 'none',
                cursor: 'pointer',
                fontWeight: activeTab === 'rings' ? 'bold' : 'normal'
              }}
            >
              💍 Rings
            </button>
          </div>
        </div>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'questions' && (
        <>
          {/* Create Question Section */}
          <div style={{ 
            marginBottom: '30px', 
            border: '1px solid #ddd',
            padding: '20px',
            backgroundColor: '#fff'
          }}>
            <h2 style={{ marginTop: 0 }}>
              {isEditMode ? 'Edit Question' : 'Create New Question'}
            </h2>

            {error && <div style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '10px',
              marginBottom: '15px'
            }}>{error}</div>}

            {isEditMode && (
              <div style={{
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                padding: '10px 15px',
                marginBottom: '15px',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                <p style={{ margin: '0', fontWeight: 'bold' }}>
                  Editing question: "{formData.questionText}"
                </p>
                <p style={{ margin: '5px 0 0 0' }}>
                  ID: {editingQuestionId}
                </p>
              </div>
            )}
            
            <form className="admin-form" onSubmit={handleSubmit}>
              <div className="form-section">
                <h3 className="section-title">Question Details</h3>
                
                <div className="form-group">
                  <label className="form-label">Question Text:</label>
                  <input
                    type="text"
                    name="questionText"
                    value={formData.questionText}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Enter your question here"
                  />
                </div>
                
                <div className="form-group" style={{ marginBottom: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                  <label className="form-label" style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#333' }}>
                    Question Type:
                  </label>
                  <select
                    name="questionType"
                    value={formData.questionType || 'standard'}
                    onChange={handleChange}
                    className="form-input"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #FF6B00',
                      borderRadius: '4px',
                      fontSize: '16px',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      appearance: 'menulist', /* Show dropdown arrow */
                      height: '45px', /* Make dropdown taller */
                    }}
                  >
                    <option value="standard">Standard Poll</option>
                    <option value="leaderboard">Take Leaderboard</option>
                  </select>
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '5px', fontWeight: 'bold' }}>
                    Leaderboard type will display top takes instead of poll results
                  </div>
                </div>

                {/* Category Assignment Dropdown */}
                <div className="form-group" style={{ marginBottom: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                  <label className="form-label" style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#333' }}>
                    Category: <span style={{ color: '#888', fontSize: '0.9em', fontWeight: 'normal' }}>(optional)</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category || ''}
                    onChange={handleChange}
                    className="form-input"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #4CAF50',
                      borderRadius: '4px',
                      fontSize: '16px',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      appearance: 'menulist', /* Show dropdown arrow */
                      height: '45px', /* Make dropdown taller */
                    }}
                  >
                    <option value="">-- Select Category (Optional) --</option>
                    {availableCategories.map(category => (
                      <option key={category.id} value={category.id.toString()} style={{ backgroundColor: category.color + '22' }}>
                        {category.emoji} {category.name}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                    Assign this question to a category section. Questions with no category will appear in the general feed only.
                  </div>
                </div>

                <div className="form-row">
                  {formData.questionType !== 'leaderboard' && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Option A:</label>
                        <input
                          type="text"
                          name="optionA"
                          value={formData.optionA}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="First option"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Option B:</label>
                        <input
                          type="text"
                          name="optionB"
                          value={formData.optionB}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Second option"
                        />
                      </div>
                    </>
                  )}

                  {formData.questionType === 'leaderboard' && (
                    <div className="form-group" style={{ width: '100%' }}>
                      <div style={{
                        backgroundColor: '#FFF4E6',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px solid #FFE0B2'
                      }}>
                        <p style={{ fontWeight: 'bold', color: '#FF6B00', marginBottom: '8px' }}>
                          Leaderboard Mode
                        </p>
                        <p style={{ fontSize: '14px', lineHeight: '1.5' }}>
                          In leaderboard mode, the system will automatically analyze takes and display the most popular answers as buttons.
                          <br/>
                          No manual options needed - the app will skim takes to find the most common responses.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    Upload Image or Video: {isEditMode && <span style={{ color: '#666', fontWeight: 'normal', fontSize: '0.9em' }}>(Optional when editing)</span>}
                  </label>
                  <div className="file-input-container">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleImageChange}
                      className="file-input"
                      id="imageUpload"
                    />
                    <label htmlFor="imageUpload" className="file-input-label">
                      Choose File
                    </label>
                    <span className="file-name">
                      {imageFile ? `${imageFile.name} ${mediaType ? `(${mediaType})` : ''}` : 'No file chosen'}
                    </span>
                  </div>
                  {isEditMode && !imageFile && (
                    <p style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                      Leave empty to keep the current media
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
                    📱 iPhone users: You can take a photo/video or choose from your library
                  </p>
                </div>

                {/* Thumbnail upload for videos */}
                {mediaType === 'video' && (
                  <div className="form-group">
                    <label className="form-label">
                      Upload Thumbnail (Optional but recommended):
                    </label>
                    <div className="file-input-container">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailChange}
                        className="file-input"
                        id="thumbnailUpload"
                      />
                      <label htmlFor="thumbnailUpload" className="file-input-label">
                        Choose Thumbnail
                      </label>
                      <span className="file-name">
                        {thumbnailFile ? thumbnailFile.name : 'No thumbnail chosen'}
                      </span>
                    </div>
                    <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
                      💡 A thumbnail shows while your video loads, providing a better user experience
                    </p>
                  </div>
                )}
              </div>

              {/* Leaderboard Options Section - only shown when questionType is 'leaderboard' */}
              {formData.questionType === 'leaderboard' && (
                <div className="form-section">
                  <h3 className="section-title">Leaderboard Settings</h3>

                  {/* Answer Whitelist Section */}
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                      Answer Whitelist:
                    </label>
                    <textarea
                      name="leaderboardOptions_whitelist"
                      value={formData.leaderboardOptions?.whitelist || ''}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="kobe, kobe bryant, mj, michael jordan, lebron, lebron james"
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        padding: '10px',
                        fontFamily: 'monospace',
                        fontSize: '14px'
                      }}
                    />
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                      <p><strong>Only the answers listed above will appear in the leaderboard.</strong></p>
                      <p>Enter comma-separated answers (e.g., <code>kobe, kobe bryant, mj, michael jordan</code>)</p>
                      <p>Include variations and nicknames for better matching. Leave blank to allow all answers.</p>
                    </div>
                  </div>

                  {/* Banned Words Section */}
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label className="form-label" style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                      Banned Words:
                    </label>
                    <textarea
                      name="leaderboardOptions_bannedWords"
                      value={formData.leaderboardOptions?.bannedWords || ''}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="inappropriate, offensive, spam"
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        padding: '10px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        border: '1px solid #ff6961'
                      }}
                    />
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                      <p><strong>Answers containing these words will be filtered out from the leaderboard.</strong></p>
                      <p>Enter comma-separated words to ban (e.g., <code>inappropriate, offensive, spam</code>)</p>
                      <p>The app already filters common offensive words - use this for additional domain-specific filtering.</p>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Show Tomato Count:</label>
                      <select
                        name="leaderboardOptions_showTomatoCount"
                        value={formData.leaderboardOptions?.showTomatoCount === false ? 'false' : 'true'}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Show Clap Count:</label>
                      <select
                        name="leaderboardOptions_showClapCount"
                        value={formData.leaderboardOptions?.showClapCount === true ? 'true' : 'false'}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Number of Takes:</label>
                      <select
                        name="leaderboardOptions_limit"
                        value={formData.leaderboardOptions?.limit || '3'}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="5">5</option>
                        <option value="10">10</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Sort By:</label>
                      <select
                        name="leaderboardOptions_sortBy"
                        value={formData.leaderboardOptions?.sortBy || 'tomatoes'}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="tomatoes">Most Tomatoes</option>
                        <option value="claps">Most Claps</option>
                        <option value="ratio">Tomato to Clap Ratio</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Button Color:</label>
                      <input
                        type="color"
                        name="leaderboardStyles_backgroundColor"
                        value={formData.leaderboardStyles?.backgroundColor || '#ffffff'}
                        onChange={handleChange}
                        className="color-picker"
                        style={{ width: '40px', height: '40px', padding: 0, marginRight: '10px' }}
                      />
                      <input
                        type="text"
                        name="leaderboardStyles_backgroundColor"
                        value={formData.leaderboardStyles?.backgroundColor || '#ffffff'}
                        onChange={handleChange}
                        className="form-input"
                        style={{ width: 'calc(100% - 50px)' }}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Text Color:</label>
                      <input
                        type="color"
                        name="leaderboardStyles_textColor"
                        value={formData.leaderboardStyles?.textColor || '#000000'}
                        onChange={handleChange}
                        className="color-picker"
                        style={{ width: '40px', height: '40px', padding: 0, marginRight: '10px' }}
                      />
                      <input
                        type="text"
                        name="leaderboardStyles_textColor"
                        value={formData.leaderboardStyles?.textColor || '#000000'}
                        onChange={handleChange}
                        className="form-input"
                        style={{ width: 'calc(100% - 50px)' }}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Border Color:</label>
                      <input
                        type="color"
                        name="leaderboardStyles_borderColor"
                        value={formData.leaderboardStyles?.borderColor || '#e0e0e0'}
                        onChange={handleChange}
                        className="color-picker"
                        style={{ width: '40px', height: '40px', padding: 0, marginRight: '10px' }}
                      />
                      <input
                        type="text"
                        name="leaderboardStyles_borderColor"
                        value={formData.leaderboardStyles?.borderColor || '#e0e0e0'}
                        onChange={handleChange}
                        className="form-input"
                        style={{ width: 'calc(100% - 50px)' }}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Border Radius:</label>
                      <input
                        type="text"
                        name="leaderboardStyles_borderRadius"
                        value={formData.leaderboardStyles?.borderRadius || '50px'}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., 50px, 8px, etc."
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="form-section">
                <h3 className="section-title">Appearance Settings</h3>

                <div className="color-section">
                  <div className="color-column">
                    <h4 className="color-title">Option A Style</h4>
                    <div className="color-picker-group">
                      <label className="form-label">Background Color:</label>
                      <div className="color-input-container">
                        <input
                          type="color"
                          name="style_optionABackgroundColor"
                          value={formData.styles.optionABackgroundColor}
                          onChange={handleChange}
                          className="color-picker"
                        />
                        <input
                          type="text"
                          name="style_optionABackgroundColor"
                          value={formData.styles.optionABackgroundColor}
                          onChange={handleChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                    
                    <div className="color-picker-group">
                      <label className="form-label">Text Color:</label>
                      <div className="color-input-container">
                        <input
                          type="color"
                          name="style_optionATextColor"
                          value={formData.styles.optionATextColor}
                          onChange={handleChange}
                          className="color-picker"
                        />
                        <input
                          type="text"
                          name="style_optionATextColor"
                          value={formData.styles.optionATextColor}
                          onChange={handleChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="color-column">
                    <h4 className="color-title">Option B Style</h4>
                    <div className="color-picker-group">
                      <label className="form-label">Background Color:</label>
                      <div className="color-input-container">
                        <input
                          type="color"
                          name="style_optionBBackgroundColor"
                          value={formData.styles.optionBBackgroundColor}
                          onChange={handleChange}
                          className="color-picker"
                        />
                        <input
                          type="text"
                          name="style_optionBBackgroundColor"
                          value={formData.styles.optionBBackgroundColor}
                          onChange={handleChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                    
                    <div className="color-picker-group">
                      <label className="form-label">Text Color:</label>
                      <div className="color-input-container">
                        <input
                          type="color"
                          name="style_optionBTextColor"
                          value={formData.styles.optionBTextColor}
                          onChange={handleChange}
                          className="color-picker"
                        />
                        <input
                          type="text"
                          name="style_optionBTextColor"
                          value={formData.styles.optionBTextColor}
                          onChange={handleChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="form-actions" style={{ display: 'flex', gap: '10px' }}>
                  {isEditMode && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      style={{
                        flex: '1',
                        padding: '12px',
                        backgroundColor: '#f5f5f5',
                        color: '#333',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    type="submit"
                    className="form-submit"
                    disabled={loading}
                    style={{
                      flex: '2',
                      padding: '12px',
                      backgroundColor: isEditMode ? '#4CAF50' : '#FF6B00',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ?
                      (isEditMode ? 'Updating Question...' : 'Creating Question...') :
                      (isEditMode ? 'Update Question' : 'Create Question')}
                  </button>
                </div>
              </div>
            </form>
          </div>
          
          {/* All Questions Section */}
          <div style={{ 
            marginTop: '30px', 
            border: '1px solid #ddd',
            padding: '20px',
            backgroundColor: '#fff'
          }}>
            <h2 style={{ 
              marginTop: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <span>All Questions</span>
              <span>Total: {allQuestions.length}</span>
            </h2>
            {loadingQuestions ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px'
              }}>
                Loading questions...
              </div>
            ) : allQuestions.length > 0 ? (
              <div>
                <div className="questions-grid" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                  gap: '20px',
                  marginBottom: '20px'
                }}>
                  {allQuestions.map(question => (
                    <div key={question.id} className="question-item" style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #eaeaea',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ height: '150px', width: '100%', overflow: 'hidden' }}>
                        {question.mediaType === 'video' ? (
                          <video
                            src={question.imageUrl}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            muted
                            loop
                            autoPlay
                            playsInline
                          />
                        ) : (
                          <img
                            src={question.imageUrl}
                            alt={question.questionText}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )}
                      </div>
                      <div style={{ padding: '15px' }}>
                        <h3 style={{ fontSize: '16px', marginBottom: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {question.questionText}
                        </h3>

                        {/* Show category badge if it exists */}
                        {question.category && (
                          <div style={{
                            display: 'inline-block',
                            backgroundColor: availableCategories.find(cat => cat.id.toString() === question.category)?.color || '#999',
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            marginBottom: '10px',
                            fontWeight: 'bold'
                          }}>
                            {availableCategories.find(cat => cat.id.toString() === question.category)?.emoji || '📂'}
                            {availableCategories.find(cat => cat.id.toString() === question.category)?.name || 'Category'}
                          </div>
                        )}

                        <div style={{ fontSize: '14px', marginBottom: '10px' }}>
                          <p><span style={{ fontWeight: 'bold' }}>A:</span> {question.optionA}</p>
                          <p><span style={{ fontWeight: 'bold' }}>B:</span> {question.optionB}</p>
                        </div>
                        <div style={{ 
                          padding: '8px', 
                          background: '#f5f5f5', 
                          borderRadius: '4px',
                          marginBottom: '15px',
                          fontSize: '14px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span>Total: <b>{question.totalVotes}</b></span>
                            <span>A: <b>{question.optionAVotes}</b></span>
                            <span>B: <b>{question.optionBVotes}</b></span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            onClick={() => handleEditQuestion(question)}
                            style={{
                              flex: '1',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              padding: '10px 15px',
                              borderRadius: '4px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '500',
                              fontSize: '0.9rem'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id, question.questionText || '')}
                            disabled={deleteLoading}
                            style={{
                              flex: '1',
                              backgroundColor: '#e53935',
                              color: 'white',
                              padding: '10px 15px',
                              borderRadius: '4px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '500',
                              fontSize: '0.9rem'
                            }}
                          >
                            {deleteLoading ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {hasMoreQuestions && (
                  <button 
                    className="load-more-button"
                    onClick={loadMoreQuestions}
                    disabled={loadingQuestions}
                    style={{
                      display: 'block',
                      margin: '0 auto',
                      padding: '10px 20px',
                      backgroundColor: '#f0f0f0',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {loadingQuestions ? 'Loading...' : 'Load More Questions'}
                  </button>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>No questions yet. Create one using the form above.</p>
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Take Management Tab */}
      {activeTab === 'comments' && (
        <div style={{
          border: '1px solid #ddd',
          padding: '20px',
          backgroundColor: '#fff'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Take Management</h2>
          <CommentManagement />
        </div>
      )}
      
      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div style={{
          border: '1px solid #ddd',
          padding: '20px',
          backgroundColor: '#fff'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px' }}>User Management</h2>
          <UserManagement />
        </div>
      )}

      {/* Global Settings Tab */}
      {activeTab === 'settings' && (
        <div style={{
          border: '1px solid #ddd',
          padding: '20px',
          backgroundColor: '#fff'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Global Application Settings</h2>
          
          {/* App Logo Upload Section */}
          <div className="form-section" style={{ marginBottom: '30px' }}>
            <h3 className="section-title" style={{
              borderBottom: '1px solid #eee',
              paddingBottom: '10px',
              marginBottom: '20px',
              color: '#4a90e2'
            }}>
              Application Logo
            </h3>
            
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div style={{ 
                width: '150px', 
                minWidth: '150px',
                marginRight: '20px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                padding: '10px'
              }}>
                {currentLogoUrl ? (
                  <img 
                    src={currentLogoUrl} 
                    alt="Current Logo" 
                    style={{ 
                      width: '100%', 
                      height: 'auto', 
                      objectFit: 'contain' 
                    }}
                  />
                ) : (
                  <span style={{ color: '#999', fontSize: '14px' }}>No logo</span>
                )}
              </div>
              
              <div style={{ flex: 1 }}>
                <form onSubmit={handleLogoUpload}>
                  <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="logo-upload" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Upload New Logo:
                    </label>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      style={{ 
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: '#777' }}>
                      Images will be automatically resized to 150x150px max. Transparent PNG preferred.
                    </small>
                  </div>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="logo-size" style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Logo Display Size: {logoSize || 100}%
                    </label>
                    <input
                      id="logo-size"
                      type="range"
                      min="20"
                      max="150"
                      step="5"
                      value={logoSize || 100}
                      onChange={(e) => setLogoSize(parseInt(e.target.value))}
                      style={{ 
                        width: '100%',
                        padding: '8px 0',
                      }}
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: '#777' }}>
                      Adjust the size of your logo as it appears in the app header.
                    </small>
                  </div>
                  
                  {logoUploadError && (
                    <div style={{
                      backgroundColor: '#ffebee',
                      color: '#c62828',
                      padding: '10px',
                      marginBottom: '15px',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                      {logoUploadError}
                    </div>
                  )}
                  
                  {logoUploadSuccess && (
                    <div style={{
                      backgroundColor: '#e8f5e9',
                      color: '#2e7d32',
                      padding: '10px',
                      marginBottom: '15px',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                      Logo uploaded successfully!
                    </div>
                  )}
                  
                  {logoSizeSaved && (
                    <div style={{
                      backgroundColor: '#e8f5e9',
                      color: '#2e7d32',
                      padding: '10px',
                      marginBottom: '15px',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                      Logo size saved successfully!
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="submit"
                      disabled={logoUploading || !logoFile}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#4a90e2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: logoUploading || !logoFile ? 'not-allowed' : 'pointer',
                        opacity: logoUploading || !logoFile ? 0.7 : 1,
                        flex: '1'
                      }}
                    >
                      {logoUploading ? 'Uploading...' : 'Upload Logo'}
                    </button>
                    
                    <button
                      type="button"
                      disabled={logoUploading || !currentLogoUrl}
                      onClick={handleSaveLogoSize}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: logoUploading || !currentLogoUrl ? 'not-allowed' : 'pointer',
                        opacity: logoUploading || !currentLogoUrl ? 0.7 : 1,
                        flex: '1'
                      }}
                    >
                      Save Size
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <form onSubmit={handleSaveGlobalSettings}>
            {/* Global Banned Words Section */}
            <div className="form-section" style={{ marginBottom: '30px' }}>
              <h3 className="section-title" style={{
                borderBottom: '1px solid #eee',
                paddingBottom: '10px',
                marginBottom: '20px',
                color: '#d32f2f'
              }}>
                Global Banned Words & Content Filtering
              </h3>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label className="form-label" style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                  App-wide Banned Words:
                </label>
                <textarea
                  name="bannedWords"
                  value={globalSettings.bannedWords}
                  onChange={handleGlobalSettingsChange}
                  className="form-input"
                  placeholder="inappropriate, offensive, spam"
                  style={{
                    width: '100%',
                    minHeight: '200px',
                    padding: '15px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    border: '1px solid #ff6961',
                    borderRadius: '4px'
                  }}
                />
                <div style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                  <p><strong>These words will be banned across the entire application for all questions.</strong></p>
                  <p>Enter comma-separated words to ban (e.g., <code>inappropriate, offensive, spam</code>)</p>
                  <p>This will be combined with the built-in list of inappropriate terms and per-question banned words.</p>
                  <p>Make sure to include common misspellings and variations for better filtering.</p>
                </div>
              </div>

              {/* Save Button */}
              <div style={{ marginTop: '30px' }}>
                <button
                  type="submit"
                  disabled={globalSettings.isLoading}
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: globalSettings.isLoading ? 'not-allowed' : 'pointer',
                    opacity: globalSettings.isLoading ? 0.7 : 1
                  }}
                >
                  {globalSettings.isLoading ? 'Saving...' : 'Save Global Settings'}
                </button>

                {/* Success Message */}
                {globalSettings.saveSuccess && (
                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}>
                    ✓ Settings saved successfully!
                  </div>
                )}

                {/* Error Message */}
                {globalSettings.saveError && (
                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}>
                    Error: {globalSettings.saveError}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Rings Tab */}
      {activeTab === 'rings' && (
        <div style={{ 
          backgroundColor: '#fff', 
          border: '1px solid #ddd', 
          borderRadius: '5px',
          padding: '20px',
          margin: '20px 0'
        }}>
          <h2 style={{ marginTop: 0, color: '#333', borderBottom: '2px solid #FF6B00', paddingBottom: '10px' }}>
            💍 Ring Management System
          </h2>
          
          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Manage profile rings for users. Create custom rings, manage the store, and create ring packs.
            </p>
          </div>

          {/* Ring Management Buttons */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* Admin Special Rings */}
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              padding: '20px',
              backgroundColor: '#f9f9f9'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>👑 Admin Special Rings</h3>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
                Create and grant special rings to users. These are exclusive rings that cannot be purchased.
              </p>
              <button 
                onClick={() => setShowAdminRingManager(true)}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  width: '100%'
                }}
              >
                Manage Admin Rings
              </button>
            </div>

            {/* Store Rings */}
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              padding: '20px',
              backgroundColor: '#f9f9f9'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>🛍️ Store Rings</h3>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
                Create rings for sale in the store. Set prices, rarities, and custom designs.
              </p>
              <button 
                onClick={() => setShowStoreRingManager(true)}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  width: '100%'
                }}
              >
                Manage Store Rings
              </button>
            </div>

            {/* Ring Packs */}
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              padding: '20px',
              backgroundColor: '#f9f9f9'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>📦 Ring Packs</h3>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
                Create discounted bundles of rings. Set limited-time offers and bulk pricing.
              </p>
              <button 
                onClick={() => setShowRingPackManager(true)}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  width: '100%'
                }}
              >
                Manage Ring Packs
              </button>
            </div>

            {/* Collections */}
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              padding: '20px',
              backgroundColor: '#f9f9f9'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>📚 Collections</h3>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '15px' }}>
                Create and manage custom ring collections. Organize rings by themes and categories.
              </p>
              <button 
                onClick={() => setShowCollectionManager(true)}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#6366F1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  width: '100%'
                }}
              >
                Manage Collections
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div style={{ 
            backgroundColor: '#f0f0f0', 
            padding: '15px', 
            borderRadius: '5px',
            border: '1px solid #ddd'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>💡 Ring System Overview</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#666' }}>
              <li><strong>Admin Special Rings:</strong> Exclusive rings granted manually to users</li>
              <li><strong>Store Rings:</strong> Individual rings available for purchase with coins</li>
              <li><strong>Ring Packs:</strong> Discounted bundles of multiple rings</li>
              <li><strong>Free Distribution:</strong> Grant rings or packs to users at no cost</li>
              <li><strong>Pricing Control:</strong> Set custom prices or use rarity-based defaults</li>
            </ul>
          </div>
        </div>
      )}

      {/* Ring Management Modals */}
      <AdminRingManager 
        isOpen={showAdminRingManager}
        onClose={() => setShowAdminRingManager(false)}
        adminUserId="admin" // You might want to get this from auth context
      />
      
      <AdminStoreRingManager 
        isOpen={showStoreRingManager}
        onClose={() => setShowStoreRingManager(false)}
        adminUserId="admin" // You might want to get this from auth context
      />
      
      <AdminRingPackManager 
        isOpen={showRingPackManager}
        onClose={() => setShowRingPackManager(false)}
        adminUserId="admin" // You might want to get this from auth context
      />
      
      <AdminCollectionManager 
        isOpen={showCollectionManager}
        onClose={() => setShowCollectionManager(false)}
        adminUserId="admin" // You might want to get this from auth context
      />
    </div>
  );
}

export default Admin;